import React, { useEffect, useRef } from "react";
import { Group, Rect, Image as KonvaImage } from "react-konva";
import useImage from "use-image";
import Konva from "konva";

// keep these wherever you already have them
const sampleImages = {
  landscape: "https://every722.s3.us-east-2.amazonaws.com/horizontal-sample.jpg",
  portrait: "https://every722.s3.us-east-2.amazonaws.com/vertical-sample.jpg",
};

const PhotoFrame = ({ obj, refCallback, onSelect, onUpdate, isSnap, grid }) => {
  const groupRef = useRef(null);
  const rectRef = useRef(null);
  const imageRef = useRef(null);

  const orientation = obj.imagePlaceholder?.orientation || "landscape";
  const previewSrc = sampleImages[orientation];
  const [image] = useImage(previewSrc || "", "anonymous");

  const w = obj.width ?? 240;
  const h = obj.height ?? 160;

  // ALWAYS cover + center
  let drawW = 0,
    drawH = 0,
    ox = 0,
    oy = 0;

  if (image) {
    const iw = image.width || 1;
    const ih = image.height || 1;

    const sCover = Math.max(w / iw, h / ih);

    drawW = iw * sCover;
    drawH = ih * sCover;

    // center crop
    ox = -(drawW - w) / 2;
    oy = -(drawH - h) / 2;
  }

  // ---- Filters (match your ImageShape behavior) ----
  const opacity = obj.opacity ?? 1;
  const brightness = obj.brightness ?? 0; // -1..1
  const contrast = obj.contrast ?? 0;     // -100..100
  const grayscale = (obj.grayscale ?? 0) > 0; // slider stored, filter is on/off
  const sepia = (obj.sepia ?? 0) > 0;         // slider stored, filter is on/off

  const filters = [
    brightness !== 0 ? Konva.Filters.Brighten : null,
    contrast !== 0 ? Konva.Filters.Contrast : null,
    grayscale ? Konva.Filters.Grayscale : null,
    sepia ? Konva.Filters.Sepia : null,
  ].filter(Boolean);

  // Re-cache image whenever filters or size changes
  useEffect(() => {
    if (!imageRef.current || !image) return;

    // critical: caching needed for Konva filters
    imageRef.current.clearCache();
    imageRef.current.cache();
    imageRef.current.getLayer()?.batchDraw();
  }, [
    image,
    w,
    h,
    opacity,
    brightness,
    contrast,
    grayscale,
    sepia,
    orientation,
  ]);

  const handleClickSelect = (e) => {
    e.cancelBubble = true;
    onSelect(e, obj.id);
  };

  const handleDragStartSelect = (e) => {
    e.cancelBubble = true;
    onSelect(e, obj.id);
  };

  return (
    <Group
      id={obj.id}
      ref={groupRef}
      x={obj.x ?? 0}
      y={obj.y ?? 0}
      rotation={obj.rotation || 0}          // ✅ rotate group so image rotates too
      draggable={obj.draggable !== false}
      dragDistance={2}
      onClick={handleClickSelect}
      onTap={handleClickSelect}
      onDragStart={handleDragStartSelect}
      onDragEnd={(e) => {
        const rawX = e.target.x();
        const rawY = e.target.y();
        const snappedX = isSnap ? Math.round(rawX / grid) * grid : rawX;
        const snappedY = isSnap ? Math.round(rawY / grid) * grid : rawY;
        onUpdate(obj.id, { x: snappedX, y: snappedY });
      }}
      // ✅ clip in local coords (inside the rotated group)
      clip={{ x: 0, y: 0, width: w, height: h }}
    >
      {/* Preview image (filters live here) */}
      {image && (
        <KonvaImage
          ref={imageRef}
          image={image}
          x={ox}
          y={oy}
          width={drawW}
          height={drawH}
          listening={false}
          opacity={opacity}
          filters={filters}
          brightness={brightness}
          contrast={contrast}
        />
      )}
      {/* Transformer target (MUST stay 0,0) */}
      <Rect
        ref={(node) => {
          rectRef.current = node;
          if (refCallback) refCallback(node); // transformer attaches here
        }}
        x={0}
        y={0}
        width={w}
        height={h}
        fill="rgba(0,0,0,0.001)"
        stroke={obj.stroke || "#000000"}
        strokeWidth={obj.strokeWidth ?? 1}
        dash={obj.dash ?? []}
        cornerRadius={obj.cornerRadius ?? 0}
        listening={true}
        onTransformEnd={(e) => {
          const rectNode = e.target;
          const groupNode = groupRef.current;

          const sx = rectNode.scaleX();
          const sy = rectNode.scaleY();

          const driftX = rectNode.x();
          const driftY = rectNode.y();

          const deltaRotation = rectNode.rotation(); // 🔥 THIS is the missing piece

          // Reset rect local transform
          rectNode.scaleX(1);
          rectNode.scaleY(1);
          rectNode.x(0);
          rectNode.y(0);
          rectNode.rotation(0);

          const nextW = Math.max(1, w * sx);
          const nextH = Math.max(1, h * sy);

          const baseX = obj.x ?? 0;
          const baseY = obj.y ?? 0;
          const baseRot = obj.rotation ?? 0;

          onUpdate(obj.id, {
            x: baseX + driftX,
            y: baseY + driftY,
            width: nextW,
            height: nextH,
            rotation: baseRot + deltaRotation, // 🔥 bake rotation into group
          });
        }}
      />
    </Group>
  );
};

export default PhotoFrame;
